/**
 * **The four acts on a client's ledger** — booking-system R14 and R15, as the one dialog the
 * Rozliczenia tab opens for each (T-C3 Doładowanie, T-C4 Rozliczenie zaległości, T-C5 Korekta,
 * T-C6 Anuluj obciążenie).
 *
 * "Which fields does this act carry, within what range, and may it be sent yet" is one question
 * with four answers, so it has one entry point ({@link ledgerActVerdict}) rather than four
 * predicates for a component to combine. The shapes are the server's
 * (`CreditLedgerProcessor.ComposeEntry`), mirrored so the trainer is told before the write:
 *
 * | act         | amount                                  | note     | session  |
 * |-------------|-----------------------------------------|----------|----------|
 * | grant       | 1 … 1000                                | optional | —        |
 * | settlement  | 1 … sessions owed (nothing owed: none)  | required | —        |
 * | adjustment  | −1000 … 1000, never zero                | required | —        |
 * | forgiveness | none — it cancels one session's cost    | optional | required |
 *
 * A settlement's ceiling is what the client owes, not the server's cap: R14 makes it the act that
 * CLOSES owed sessions, and settling more than is owed is a grant wearing a settlement's words.
 *
 * ── The request id ─────────────────────────────────────────────────────────────────────────────
 * Minted when the dialog opens and carried by every edit, so a resend of the same act — a retry
 * after a dropped answer — converges on the one entry it may already have written (R14, R15). It is
 * never minted again inside one dialog: a refusal as already used (`idempotency_key_reused`)
 * means an earlier send landed, and closes the act (`ledgerRefusal`). The id arrives as a
 * parameter, so this module stays pure.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesBookingDtosRequestsPostCreditLedgerEntryRequest as LedgerEntryRequest } from '~/shared/api/generated/model';

import { CREDIT_REFUSAL_KEYS, type FailedWrite } from './creditRefusal';
import { standingOfBalance, type CreditStanding } from './standing';

export const LEDGER_ACTS = ['grant', 'settlement', 'adjustment', 'forgiveness'] as const;

export type LedgerAct = (typeof LEDGER_ACTS)[number];

/** `BookingDefaults.MaxLedgerAmount`. */
export const MAX_LEDGER_AMOUNT = 1000;

/** `PostCreditLedgerEntryRequest.NoteMaxLength`. */
export const LEDGER_NOTE_MAX_LENGTH = 500;

export interface LedgerActDraft {
  readonly act: LedgerAct;
  /** Signed. Zero for a forgiveness, which moves no amount. */
  readonly amount: number;
  readonly note: string;
  /** The session a forgiveness names, and nothing else's field. */
  readonly calendarEventId: number | null;
  readonly requestId: string;
}

/** Where the client stands, as the server stated it — both figures off one response. */
export interface LedgerStanding {
  readonly balance: number;
  readonly sessionsOwed: number;
}

/** What still stops the act, in the order a person fills the dialog in. */
export type LedgerActBlocker = 'nothingOwed' | 'zero' | 'amount' | 'note' | 'session';

export interface LedgerActVerdict {
  /** The amount's bounds, or `null` for an act that carries none. */
  readonly range: { readonly min: number; readonly max: number } | null;
  readonly noteRequired: boolean;
  readonly blockedBy: LedgerActBlocker | null;
  /** Where the client would stand after the act — `null` until there is one to send. */
  readonly after: CreditStanding | null;
}

export function openLedgerAct(act: LedgerAct, requestId: string): LedgerActDraft {
  return {
    act,
    amount: act === 'forgiveness' ? 0 : 1,
    note: '',
    calendarEventId: null,
    requestId,
  };
}

export function ledgerAmountRange(
  act: LedgerAct,
  sessionsOwed: number,
): { readonly min: number; readonly max: number } | null {
  if (act === 'forgiveness') return null;
  if (act === 'settlement') return sessionsOwed > 0 ? { min: 1, max: sessionsOwed } : null;
  return act === 'adjustment'
    ? { min: -MAX_LEDGER_AMOUNT, max: MAX_LEDGER_AMOUNT }
    : { min: 1, max: MAX_LEDGER_AMOUNT };
}

/** An amount held inside the act's own range; a whole number, whatever was typed. */
export function withAmount(draft: LedgerActDraft, amount: number, owed: number): LedgerActDraft {
  const range = ledgerAmountRange(draft.act, owed);
  if (range === null || !Number.isFinite(amount)) return draft;
  return { ...draft, amount: Math.max(range.min, Math.min(range.max, Math.trunc(amount))) };
}

export function withNote(draft: LedgerActDraft, note: string): LedgerActDraft {
  return { ...draft, note: note.slice(0, LEDGER_NOTE_MAX_LENGTH) };
}

export function withSession(draft: LedgerActDraft, calendarEventId: number): LedgerActDraft {
  return { ...draft, calendarEventId };
}

const noteRequired = (act: LedgerAct) => act === 'settlement' || act === 'adjustment';

export function ledgerActVerdict(
  draft: LedgerActDraft,
  standing: LedgerStanding,
): LedgerActVerdict {
  const owed = Math.max(0, standing.sessionsOwed);
  const range = ledgerAmountRange(draft.act, owed);
  const required = noteRequired(draft.act);
  const blockedBy = firstBlocker(draft, range, owed, required);
  const movement =
    draft.act === 'forgiveness' ? (draft.calendarEventId === null ? null : 1) : draft.amount;
  const after =
    movement === null || movement === 0 || (draft.act !== 'forgiveness' && range === null)
      ? null
      : standingOfBalance(standing.balance + movement);
  return { range, noteRequired: required, blockedBy, after };
}

function firstBlocker(
  draft: LedgerActDraft,
  range: { readonly min: number; readonly max: number } | null,
  owed: number,
  required: boolean,
): LedgerActBlocker | null {
  if (draft.act === 'forgiveness') return draft.calendarEventId === null ? 'session' : null;
  if (draft.act === 'settlement' && owed === 0) return 'nothingOwed';
  if (range === null) return 'amount';
  if (draft.amount === 0) return 'zero';
  if (draft.amount < range.min || draft.amount > range.max) return 'amount';
  if (required && draft.note.trim() === '') return 'note';
  return null;
}

/** The body for the act's own route: a field another act owns is absent, never null-and-ignored. */
export function ledgerActBody(draft: LedgerActDraft): LedgerEntryRequest {
  const trimmed = draft.note.trim();
  const note = trimmed === '' ? null : trimmed;
  return draft.act === 'forgiveness'
    ? { calendarEventId: draft.calendarEventId, note, requestId: draft.requestId }
    : { amount: draft.amount, note, requestId: draft.requestId };
}

// ── Refusals ─────────────────────────────────────────────────────────────────────────────────

/**
 * What a refused or unanswered act leaves the dialog to do.
 *
 * - `said`: stay open and say why — under the field the server named, when it named one.
 * - `alreadySaved`: the request id is spent on an entry the server holds (`idempotency_key_reused`):
 *   an earlier send landed. The dialog closes and the tab reads again; a fresh id here would arm
 *   the same act a second time on an append-only history.
 * - `unreached`: no answer came back, so it is not known whether the entry landed — and no read
 *   can say: the ledger is derived on every read and has other writers. The draft that went out
 *   IS the draft: its fields lock, and the one way on is to send it again under the SAME request
 *   id, which the server converges onto the entry it may already hold (R14).
 */
export type LedgerRefusal =
  | {
      readonly kind: 'said';
      readonly key: string;
      readonly field: LedgerField | null;
      /** The tab's reads may be behind the server; read them again. */
      readonly refetch: boolean;
    }
  | { readonly kind: 'alreadySaved' }
  | { readonly kind: 'unreached' };

export type LedgerField = 'amount' | 'note' | 'calendarEventId';

const SAVE_ERROR = 'web:booking.trainer.credits.saveError';

/** The field words, for a `booking_value_not_allowed` the server tied to one field. */
const FIELD_WORDS: Readonly<Record<LedgerField, string>> = {
  amount: 'amount',
  note: 'note',
  calendarEventId: 'session',
};

const isLedgerField = (value: string | undefined): value is LedgerField =>
  value === 'amount' || value === 'note' || value === 'calendarEventId';

/** Why the server refused an act, in the mobile app's words for the same refusals. */
export function ledgerRefusal(failure: FailedWrite | null): LedgerRefusal {
  const said = (key: string, field: LedgerField | null = null, refetch = false): LedgerRefusal => ({
    kind: 'said',
    key,
    field,
    refetch,
  });
  if (failure === null) return { kind: 'unreached' };
  switch (failure.code) {
    case 'idempotency_key_reused':
      return { kind: 'alreadySaved' };
    case 'credits_disabled':
      return said(CREDIT_REFUSAL_KEYS.credits_disabled, null, true);
    case 'booking_value_out_of_range':
      return said(`${SAVE_ERROR}.amount.text`, 'amount');
    case 'booking_value_not_allowed':
      return isLedgerField(failure.field)
        ? said(
            `${SAVE_ERROR}.${FIELD_WORDS[failure.field]}.text`,
            failure.field,
            failure.field === 'calendarEventId',
          )
        : said(`${SAVE_ERROR}.generic.text`);
    default:
      break;
  }
  if (failure.status === 404) return said(`${SAVE_ERROR}.notFound.text`, null, true);
  if (failure.status === 429) return said(`${SAVE_ERROR}.rateLimited.text`);
  return said(`${SAVE_ERROR}.generic.text`);
}
